import {
  DispatchNotificationDecision,
  DispatchNotificationResult,
  extractErrorMessage,
  useCreateDispatchNotificationMutation,
  useCreateDispatchNotificationsMutation,
  useLazyPreviewDispatchNotificationsQuery,
} from "@ots-frontend/ots-data";
import {BellAlertIcon} from "@heroicons/react/20/solid";
import {useEffect, useState} from "react";
import {OtsAsyncActionButton} from "../components/OtsButton";
import {OtsDateTime} from "../components/OtsDateTime";
import {OtsDialog} from "../components/OtsDialog";
import {OtsInlineAlert} from "../components/OtsInlineAlert";
import {OtsSwitch} from "../components/OtsSwitch";
import {StatusPill, StatusTone} from "../components/StatusPill";
import {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";
import {getNotificationSkipReasonLabel} from "./dispatch-notification-labels";

export type DispatchNotifyOverrides = {cooldown: boolean; quietHours: boolean};

/** One dispatch in the dialog: what the server would do, and what it did once sent. */
export type DispatchNotifyEntry = {
  dispatchId: string;
  /** How the dispatch is named in the list, e.g. its work order. */
  label?: string;
  decision?: DispatchNotificationDecision;
  result?: DispatchNotificationResult;
};

export type DispatchNotifyTotals = {queued: number; skipped: number; failed: number};

export type DispatchNotifyDialogViewProps = {
  open: boolean;
  onClose: () => void;
  entries: DispatchNotifyEntry[];
  previewLoading?: boolean;
  overrides: DispatchNotifyOverrides;
  onOverridesChange: (overrides: DispatchNotifyOverrides) => void;
  sending?: boolean;
  /** Present once sent. */
  totals?: DispatchNotifyTotals;
  error?: string;
  onSend: () => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

const skipText = (reason?: string) => getNotificationSkipReasonLabel(reason) ?? "not eligible";

/** The status shown beside a dispatch before and after sending. */
export const getDispatchNotifyEntryStatus = (entry: DispatchNotifyEntry): {label: string; tone: StatusTone} => {
  if (entry.result) {
    switch (entry.result.status) {
      case "QUEUED":
        return {label: "Sent", tone: "success"};
      case "FAILED":
        return {label: "Failed", tone: "danger"};
      default:
        return {label: `Skipped: ${skipText(entry.result.skipReason)}`, tone: "warning"};
    }
  }
  if (!entry.decision) return {label: "Checking…", tone: "neutral"};
  return entry.decision.decision === "SKIP"
    ? {label: `Would skip: ${skipText(entry.decision.skipReason)}`, tone: "warning"}
    : {label: "Will send", tone: "info"};
};

const SingleSummary = ({entry, previewLoading, totals}: {
  entry?: DispatchNotifyEntry;
  previewLoading?: boolean;
  totals?: DispatchNotifyTotals;
}) => {
  const result = entry?.result;
  const decision = entry?.decision;

  if (totals) {
    const sent = totals.queued > 0;
    return <OtsInlineAlert
      tone={sent ? "success" : totals.failed ? "danger" : "warning"}
      size="compact"
      title={sent ? `Sent to ${result?.driverName ?? "the driver"}` : totals.failed ? "Sending failed" : "Not sent"}>
      {!sent && result?.skipReason && `Skipped: ${skipText(result.skipReason)}.`}
      {!sent && result?.nextAllowedAtDttm && <> You can try again after <OtsDateTime value={result.nextAllowedAtDttm}/>.</>}
    </OtsInlineAlert>;
  }
  if (previewLoading) return <p role="status" className="text-[var(--ots-text-muted)]">Checking whether the driver can be notified…</p>;
  if (!decision) return null;

  const willSkip = decision.decision === "SKIP";
  return <OtsInlineAlert
    tone={willSkip ? "warning" : "info"}
    size="compact"
    title={willSkip ? `Would be skipped: ${skipText(decision.skipReason)}` : `Ready to notify ${decision.driverName ?? "the driver"}`}>
    {willSkip && decision.nextAllowedAtDttm && <>Next allowed <OtsDateTime value={decision.nextAllowedAtDttm}/>. </>}
    {willSkip ? "Overrides below can send it anyway when the rule allows." : "Cooldowns, quiet hours and rate limits still apply."}
  </OtsInlineAlert>;
};

const BatchSummary = ({entries, previewLoading, totals}: {
  entries: DispatchNotifyEntry[];
  previewLoading?: boolean;
  totals?: DispatchNotifyTotals;
}) => {
  const willSend = entries.filter(entry => entry.decision && entry.decision.decision !== "SKIP").length;
  return <div className="space-y-3">
    {totals
      ? <OtsInlineAlert
        tone={totals.failed ? "danger" : totals.skipped ? "warning" : "success"}
        size="compact"
        title={`${totals.queued} sent, ${totals.skipped} skipped, ${totals.failed} failed`}/>
      : previewLoading
      ? <p role="status" className="text-[var(--ots-text-muted)]">Checking which drivers can be notified…</p>
      : <OtsInlineAlert tone={willSend === entries.length ? "info" : "warning"} size="compact" title={`${willSend} of ${entries.length} will be sent`}>
        Cooldowns, quiet hours and rate limits still apply; overrides below can send skipped ones when the rule allows.
      </OtsInlineAlert>}
    <ul aria-label="Dispatches to notify" className="max-h-64 divide-y divide-[var(--ots-outline-variant)] overflow-y-auto rounded-md border border-[var(--ots-outline-variant)]">
      {entries.map(entry => {
        const status = getDispatchNotifyEntryStatus(entry);
        const driver = entry.result?.driverName ?? entry.decision?.driverName;
        return <li key={entry.dispatchId} className="flex items-center justify-between gap-3 px-3 py-2">
          <div className="min-w-0">
            <p className="truncate font-medium text-[var(--ots-secondary)]">{entry.label ?? entry.dispatchId}</p>
            <p className="truncate text-xs text-[var(--ots-text-muted)]">{driver ?? "No driver"}</p>
          </div>
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </li>;
      })}
    </ul>
  </div>;
};

/** Sends driver notifications for one or more dispatches, after showing whether each would be delivered. */
export const DispatchNotifyDialogView = ({
  open,
  onClose,
  entries,
  previewLoading,
  overrides,
  onOverridesChange,
  sending,
  totals,
  error,
  onSend,
  brand,
  colorScheme,
}: DispatchNotifyDialogViewProps) => {
  const batch = entries.length > 1;

  return (
    <OtsDialog
      open={open}
      onClose={onClose}
      title={batch ? `Notify ${entries.length} drivers` : "Notify driver"}
      description={batch
        ? "Push a notification about each selected dispatch to its driver's phone."
        : "Push a notification about this dispatch to the driver's phone."}
      size="lg"
      compact
      brand={brand}
      colorScheme={colorScheme}
      closeLabel={totals ? "Done" : "Cancel"}
      actions={!totals && (
        <OtsAsyncActionButton
          size="compact"
          variant="primary"
          icon={<BellAlertIcon/>}
          isLoading={!!sending}
          loadingLabel="Sending"
          disabled={previewLoading || !entries.length}
          onClick={onSend}>
          {batch ? "Send notifications" : "Send notification"}
        </OtsAsyncActionButton>
      )}>
      <div className="space-y-4 text-sm">
        {error && <OtsInlineAlert tone="danger" size="compact" title="The notification could not be sent">{error}</OtsInlineAlert>}

        {batch
          ? <BatchSummary entries={entries} previewLoading={previewLoading} totals={totals}/>
          : <SingleSummary entry={entries[0]} previewLoading={previewLoading} totals={totals}/>}

        {!totals && (
          <div className="space-y-3">
            <OtsSwitch
              size="compact"
              checked={overrides.cooldown}
              onCheckedChange={(cooldown) => onOverridesChange({...overrides, cooldown})}
              label="Ignore cooldown"
              description="Send even if a driver was notified about the dispatch a moment ago."/>
            <OtsSwitch
              size="compact"
              checked={overrides.quietHours}
              onCheckedChange={(quietHours) => onOverridesChange({...overrides, quietHours})}
              label="Ignore quiet hours"
              description="Send even during a driver's quiet hours."/>
          </div>
        )}
      </div>
    </OtsDialog>
  );
};

export type DispatchNotifyDialogProps = {
  dispatchIds: string[];
  /** Names for the dispatches in the list, keyed by id; the id shows when missing. */
  labels?: Record<string, string>;
  open: boolean;
  onClose: () => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

const DEFAULT_OVERRIDES: DispatchNotifyOverrides = {cooldown: false, quietHours: true};

/**
 * Data-connected notify dialog for the dispatch profile and the dispatch board: previews on open, then sends
 * one notification, or a batch when several dispatches are selected.
 */
export const DispatchNotifyDialog = ({dispatchIds, labels, open, onClose, brand, colorScheme}: DispatchNotifyDialogProps) => {
  const [triggerPreview, previewState] = useLazyPreviewDispatchNotificationsQuery();
  const [createOne, createOneState] = useCreateDispatchNotificationMutation();
  const [createMany, createManyState] = useCreateDispatchNotificationsMutation();
  const [overrides, setOverrides] = useState(DEFAULT_OVERRIDES);
  const [results, setResults] = useState<Record<string, DispatchNotificationResult>>();
  const [totals, setTotals] = useState<DispatchNotifyTotals>();
  const [error, setError] = useState<string>();
  const idsKey = dispatchIds.join(",");

  useEffect(() => {
    if (!open || !idsKey) return;
    setOverrides(DEFAULT_OVERRIDES);
    setResults(undefined);
    setTotals(undefined);
    setError(undefined);
    triggerPreview({dispatchIds: idsKey.split(",")})
      .unwrap()
      .catch((cause) => setError(extractErrorMessage(cause)));
  }, [idsKey, open, triggerPreview]);

  const send = async () => {
    setError(undefined);
    const req = {overrideCooldown: overrides.cooldown, overrideQuiteHours: overrides.quietHours};
    try {
      if (dispatchIds.length === 1) {
        const response = await createOne({id: dispatchIds[0], req}).unwrap();
        setResults({[response.dispatchId]: response.result});
        setTotals(response);
      } else {
        const response = await createMany({dispatchIds, ...req}).unwrap();
        setResults(Object.fromEntries(response.results.map(entry => [entry.dispatchId, entry.result])));
        setTotals(response);
      }
    } catch (cause) {
      setError(extractErrorMessage(cause));
    }
  };

  const decisions = previewState.data?.results;
  const entries: DispatchNotifyEntry[] = dispatchIds.map(dispatchId => ({
    dispatchId,
    label: labels?.[dispatchId],
    decision: decisions?.find(entry => entry.dispatchId === dispatchId)?.decision,
    result: results?.[dispatchId],
  }));

  return (
    <DispatchNotifyDialogView
      open={open}
      onClose={onClose}
      entries={entries}
      previewLoading={previewState.isFetching}
      overrides={overrides}
      onOverridesChange={setOverrides}
      sending={createOneState.isLoading || createManyState.isLoading}
      totals={totals}
      error={error}
      onSend={send}
      brand={brand}
      colorScheme={colorScheme}/>
  );
};
